<template>
    <HeaderComponent :showDim="false" />

    <RoomPlanViewComponent
        ref="roomPlanViewComponent"
        class="view-container"
        :style="{ left: roomPlanViewLeft }"
        :transferBillOptions="transferBillOptions"
        @tableSelected="onTableSelected"
        @multipleOpenBillsTableSelected="onMultipleOpenBillsTableSelected"
    />

    <OpenBillsForTableModal
        v-if="selectedTableId"
        ref="openBillsForTableModalComponent"
        :tableId="selectedTableId"
        @openBillSelected="onOpenBillSelected"
        @closed="onOpenBillsForTableModalClosed"
    />
</template>

<script setup lang="ts">
    import type { OpenBillSelectedEventData } from "@/data/model/Bill";
    import type { IFulfillmentChangedEvent } from "@/data/model/Events";
    import type { IOperatorFulfillment, IRoomPlanTable } from "@/data/model/Operator";
    import type { TransferBillOptions } from "@/data/model/Table";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { FulfillmentSoundNotifications } from "@/operator/ux/SoundNotifications";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

    import RoomPlanViewComponent from "@/components/roomPlan/RoomPlanViewComponent.vue";
    import OpenBillsForTableModal from "@/operator/components/bill/OpenBillsForTableModal.vue";
    import HeaderComponent from "@/operator/components/layout/HeaderComponent.vue";

    // define properties
    const props = defineProps<{
        showSettingsRoomPlan?: boolean;
        roomId?: string;
        transferBillOptions?: TransferBillOptions;
    }>();

    const reservationMode = ref<boolean>(false);
    const roomPlanViewLeft = computed(() => {
        if (reservationMode.value === true) return "550px";
        else return "0px";
    });

    const selectedTableId = ref<string | undefined>();

    const roomPlanViewComponent = ref<InstanceType<typeof RoomPlanViewComponent> | null>(null);
    const openBillsForTableModalComponent = ref<InstanceType<typeof OpenBillsForTableModal> | null>(null);

    function playNewOrderForCurrentRoomSound(fulfillment: IOperatorFulfillment | IFulfillmentChangedEvent) {
        // if new order is for current room - play the notification sound
        const currentRoom = roomPlanViewComponent.value?.getCurrentRoom();
        if (currentRoom?.id == fulfillment.roomId) {
            FulfillmentSoundNotifications.playSoundIfNeeded(fulfillment);
        }
    }

    function onTableSelected(table: IRoomPlanTable) {
        operatorEvents.showOrderAndPayView.dispatch({
            tableId: table.id,
            billId: undefined,
        });
    }

    async function onMultipleOpenBillsTableSelected(table: IRoomPlanTable) {
        selectedTableId.value = table.id;
        await nextTick();
        openBillsForTableModalComponent.value?.show();
    }

    function onOpenBillsForTableModalClosed() {
        selectedTableId.value = undefined;
    }

    function onOpenBillSelected(openBill: OpenBillSelectedEventData) {
        openBillsForTableModalComponent.value?.close();

        operatorEvents.showOrderAndPayView.dispatch({
            tableId: openBill.tableId,
            billId: openBill.selectedOpenBill.id,
        });
    }

    async function onSettingsChanged() {
        if (!roomPlanViewComponent.value?.isTransferBillMode) {
            const location = OperatorClient.context.settings?.location!;
            const room = getDefaultRoom();
            await roomPlanViewComponent.value?.loadRoomPlan({ location, room });
        }
    }

    function getDefaultRoom() {
        return OperatorClient.context.settings?.planRoom!;
    }

    function onSideMenuShow() {
        reservationMode.value = true;
    }

    function onSideMenuHide() {
        reservationMode.value = false;
    }

    onMounted(async () => {
        operatorEvents.context.settingsChanged.add(onSettingsChanged);
        operatorEvents.productionContext.FulfillmentAdded.add(playNewOrderForCurrentRoomSound);
        operatorEvents.productionContext.FulfillmentChanged.add(playNewOrderForCurrentRoomSound);
        operatorEvents.sideMenu.onShow.add(onSideMenuShow);

        operatorEvents.sideMenu.onHide.add(onSideMenuHide);

        // room can be passed as param or taken from settings
        const location = OperatorClient.context.settings?.location!;
        if (props.showSettingsRoomPlan) {
            const room = getDefaultRoom();
            await roomPlanViewComponent.value?.loadRoomPlan({ location, room });
        } else {
            await roomPlanViewComponent.value?.loadRoomPlan({ location, roomId: props.roomId });
        }
    });

    onBeforeUnmount(() => {
        operatorEvents.context.settingsChanged.remove(onSettingsChanged);
        operatorEvents.productionContext.FulfillmentAdded.remove(playNewOrderForCurrentRoomSound);
        operatorEvents.productionContext.FulfillmentChanged.remove(playNewOrderForCurrentRoomSound);

        operatorEvents.sideMenu.onHide.remove(onSideMenuHide);
        operatorEvents.sideMenu.onShow.remove(onSideMenuShow);
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    // ts-1025: Font size too small in Operator App
    // .room.landscape {
    //     .room-plan-table {
    //         .inner {
    //             font-size: 11px;
    //             font-size: 1vmin;

    //             @media (orientation: landscape) {
    //                 font-size: 11px;
    //                 font-size: min(1.1vmin, 11px);
    //             }

    //             @media (orientation: portrait) {
    //                 font-size: 11px;
    //                 font-size: min(0.9vmin, 11px);
    //             }
    //         }
    //     }
    // }

    // .room.square {
    //     .room-plan-table {
    //         .inner {
    //             font-size: 11px;
    //             font-size: 1vmin;

    //             @media (orientation: landscape) {
    //                 font-size: 11px;
    //                 font-size: 1vmin;
    //                 font-size: clamp(5px, 1vmin, 11px);
    //             }

    //             @media (orientation: portrait) {
    //                 font-size: 11px;
    //                 font-size: min(1.1vmin, 11px);
    //             }
    //         }
    //     }
    // }

    // .room.portrait {
    //     .room-plan-table {
    //         .inner {
    //             font-size: 11px;
    //             font-size: 1vmin;

    //             @media (orientation: landscape) {
    //                 font-size: 11px;
    //                 font-size: min(1vmin, 11px);
    //             }

    //             @media (orientation: portrait) {
    //                 font-size: 11px;
    //                 font-size: min(1.2vh, 11px);
    //             }
    //         }
    //     }
    // }
</style>
